import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "@/components/ThemeToggle";
import { Wordmark, BrandIcon } from "@/components/Brand";

const navLinks = [
  { label: "תכונות", href: "/features/" },
  { label: "מחירים", href: "/pricing/" },
  { label: "מדריכים", href: "/articles/" },
  { label: "אודות", href: "/about/" },
  { label: "צור קשר", href: "/contact/" },
];

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-2 glass shadow-lg shadow-primary/5"
          : "py-4 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo + compliance badge */}
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 group">
            <BrandIcon size={40} className="rounded-xl shadow-lg shadow-primary/30 group-hover:shadow-primary/50 transition-shadow" />
            <Wordmark className="text-2xl text-foreground" />
          </Link>
          <span className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary border border-primary text-white text-xs font-bold whitespace-nowrap">
            <Shield className="w-3.5 h-3.5" /> תואם רשות המסים
          </span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                location.pathname.replace(/\/$/, "") === link.href.replace(/\/$/, "")
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Button asChild variant="ghost" className="rounded-xl font-medium"><Link to="/login">
              התחברות
            </Link></Button>
          <Button asChild className="rounded-xl gradient-primary text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all font-medium"><Link to="/register">
              הרשמה חינם
            </Link></Button>
        </div>

        {/* Mobile actions */}
        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle />
          <button
          className="p-2 rounded-xl hover:bg-muted transition"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "סגירת התפריט" : "פתיחת התפריט"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-border/50 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-xl text-sm font-medium text-foreground hover:bg-muted transition"
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-border/50 flex gap-2">
                <Button asChild variant="outline" className="w-full rounded-xl"><Link to="/login" className="flex-1" onClick={() => setMobileOpen(false)}>התחברות</Link></Button>
                <Button asChild className="w-full rounded-xl gradient-primary text-white"><Link to="/register" className="flex-1" onClick={() => setMobileOpen(false)}>הרשמה</Link></Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}